import { fromJS } from "immutable";
import AppConstants from "@/constants/AppConstants";
import { elementSyncUtils } from "../SyncUtils";
import { isDeviation } from "../InheritanceUtils";
import { IS_DEVIATION } from "../../constants/DesignConstants.mjs";
import { MERGE, SPLIT } from "@/constants/PropUpdateConstants.mjs";
import { getProp, inheritProp, shouldPropInherit } from "../Merge/MergeUtils.mjs";
import { LAYOUT_DESKTOP, LAYOUT_MOBILE, LAYOUTS } from "../../constants/DesignConstants.mjs";

const { LOCK_EDITING_KEY } = AppConstants;

/**
 * Computes which effects are customized for a mapped element.
 * An effect is considered customized if:
 * - For unsynced (split) properties: either layout has an explicit value (not inherited)
 * - Otherwise: either layout has a non-default value
 *
 * @param {import("immutable").Map} mappedElem - The mapped element with desktop/mobile properties
 * @param {Array<string>} effectsList - List of effect property names to check
 * @returns {Array<string>} Array of customized effect property names
 */
export const computeCustomizedEffects = (mappedElem, effectsList) => {
  const desktopProps = mappedElem.get(LAYOUT_DESKTOP);
  const mobileProps = mappedElem.get(LAYOUT_MOBILE);

  if (!desktopProps || !mobileProps) {
    return [];
  }

  const desktopList = desktopProps.toJS ? desktopProps.toJS() : desktopProps;
  const mobileList = mobileProps.toJS ? mobileProps.toJS() : mobileProps;

  const findProp = (list, propName) => list?.find?.((p) => p.name === propName);

  return effectsList.filter((propName) => {
    const desktopProp = findProp(desktopList, propName);
    const mobileProp = findProp(mobileList, propName);

    if (!desktopProp?.isSynced) {
      if (!desktopProp?.isInherited || !mobileProp?.isInherited) {
        return true;
      }
    }

    return desktopProp?.isAtDefaultValue === false || mobileProp?.isAtDefaultValue === false;
  });
};

/**
 * Prepares event handlers for property inputs
 * @param {Array<string>} inputEventHandlers - Array of handler names (e.g., ["onSIInput", "onSIChange"])
 * @param {Function} onSIInput - Input handler function
 * @param {Function} onSIChange - Change handler function
 * @param {Array} updaterArguments - Arguments to pass to handler functions
 * @returns {Object} Object with prepared handlers
 */
export const prepareEventHandlers = (
  inputEventHandlers,
  onSIInput,
  onSIChange,
  updaterArguments,
) => {
  const handlers = {};
  inputEventHandlers.forEach((callbackName) => {
    const handler = callbackName === "onSIInput" ? onSIInput : onSIChange;
    handlers[callbackName] = handler(...updaterArguments);
  });
  return handlers;
};

/**
 * Maps a property for an element, determining its value, handlers, and metadata
 *
 * @param {Object} props - The properties for mapping
 * @param {import("immutable").Map} props.element - The element map
 * @param {boolean=} props.canMerge - Prop supplied by control meta to hide split merge ui
 * @param {string} props.prop - The property name to map
 * @param {string} props.layout - The layout context (e.g., mobile, desktop)
 * @param {Function} props.isSynced - Function to check if the property is synced
 * @param {import("immutable").Map} props.theme - The theme map for styling
 * @param {Function} props.onSIInput - Handler for input events
 * @param {Function} props.onSIChange - Handler for change events
 * @param {Function=} props.removeCustomization - Remove customization function
 * @param {Function} props.toggleSyncedProp - Function to toggle the synced state of a property
 * @param {Object} props.metaMap - Property meta map with control configurations
 * @param {Object} props.defaultValues - Default values for properties
 * @param {typeof inheritProp=} props.resolveValue - Reads the displayed value; defaults to `inheritProp`
 * @returns {Object} - Mapped property details including value and event handlers
 */
export const mapProp = ({
  element,
  canMerge,
  prop,
  layout,
  isSynced,
  theme,
  onSIInput,
  onSIChange,
  removeCustomization,
  toggleSyncedProp,
  metaMap,
  defaultValues,
  resolveValue = inheritProp,
}) => {
  const name = prop;
  const propIsSynced = isSynced(prop);

  const elementValue = getProp(element, layout, prop);

  const isInherited = elementValue === undefined;
  const isInheritedFromTheme = shouldPropInherit(element, layout, prop, theme);
  const isDefault = isInherited && !isInheritedFromTheme;

  const value = resolveValue(element, layout, prop, theme) ?? defaultValues[prop];

  const isAtDefaultValue =
    defaultValues[prop] !== undefined ? value === defaultValues[prop] : true;

  const controlMeta = metaMap[prop] || {};
  const {
    updateCallback,
    inputEventHandlers = [],
    children = [],
    context = true,
    sideEffects = {},
  } = controlMeta;

  const processedSideEffects = {};
  Object.entries(sideEffects).forEach(([propName, process]) => {
    processedSideEffects[propName] = process(element, layout);
  });

  const updaterArguments = [
    name,
    value,
    propIsSynced ? LAYOUT_DESKTOP : layout,
    element,
    updateCallback,
  ];

  const preparedHandlers = prepareEventHandlers(
    inputEventHandlers,
    onSIInput,
    onSIChange,
    updaterArguments,
  );

  const subProps = {};
  children.forEach((childPropName) => {
    subProps[childPropName] = mapProp({
      element,
      canMerge: false,
      prop: childPropName,
      layout,
      isSynced: () => propIsSynced,
      theme,
      onSIInput,
      onSIChange,
      toggleSyncedProp,
      metaMap,
      defaultValues,
      resolveValue,
    });
  });

  return {
    canMerge,
    name,
    value,
    isInherited,
    isInheritedFromTheme,
    isDefault,
    isAtDefaultValue,
    isSynced: propIsSynced,
    reserveContext: true,
    context,
    subProps,
    toggleSync: () => toggleSyncedProp(prop, value, propIsSynced ? LAYOUT_DESKTOP : layout),
    controlMeta,
    children,
    removeCustomization,
    label: controlMeta.label,
    isLocked: element.get(LOCK_EDITING_KEY) === "1",
    ...processedSideEffects,
    ...preparedHandlers,
  };
};

/**
 * @callback PropertyMapperFunction
 * @param {import("immutable").Map} element - The element map to map properties for
 * @param {Function} onSIChange - Handler for change events
 * @param {Function} onSIInput - Handler for input events
 * @param {import("immutable").Map} theme - The theme map for styling
 * @param {string} layout - The layout context (e.g., mobile, desktop)
 * @param {Function} onToggleSync - Function to toggle the synced state of a property
 * @param {Function} onCustomizationRemoval - Function to remove customization for a property
 * @param {Object=} metaMapOverride - Optional override for the property meta map
 * @returns {import("immutable").Map} A mapped element structure with properties organized by layout
 */

/**
 * Generic property mapper for non-button elements
 * Creates a function that maps element properties organized by layout
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.propertyMetaMap - Map of property names to their control metadata
 * @param {Array<string>} config.customizableEffects - Array of effect property names to map
 * @param {Object} config.defaultValues - Default values for properties
 * @param {typeof inheritProp=} config.resolveValue - Reads each prop's displayed value; see `mapProp`
 * @returns {PropertyMapperFunction} A mapper function that maps element properties organized by layout
 */
export const createPropertyMapper = (config) => {
  const { propertyMetaMap, customizableEffects, defaultValues, resolveValue } = config;

  return (
    element,
    onSIChange,
    onSIInput,
    theme,
    layout,
    onToggleSync,
    onCustomizationRemoval,
    metaMapOverride,
  ) => {
    let mappedElem = fromJS({});

    mappedElem = mappedElem
      .set("data", element)
      .set("type", element.get("type"))
      .set("label", element.get("label"))
      .set("id", element.get("id"))
      .set("isLocked", element.get(LOCK_EDITING_KEY) === "1")
      .set(IS_DEVIATION, isDeviation(element));

    const { isSynced } = elementSyncUtils(element, onSIChange, layout, undefined, undefined, theme);

    const toggleSyncedProp = (propName, value, layout) => {
      const propIsSynced = isSynced(propName);
      const propUpdate = {
        name: propName,
        intent: propIsSynced ? SPLIT : MERGE,
        value,
        layout,
      };

      onToggleSync(propUpdate);
    };

    const metaMap = metaMapOverride || propertyMetaMap;

    LAYOUTS.forEach((layout) => {
      const effectsData = customizableEffects.map((prop) =>
        mapProp({
          element,
          prop,
          layout,
          isSynced,
          theme,
          onSIInput,
          onSIChange,
          removeCustomization: () => onCustomizationRemoval(prop),
          toggleSyncedProp,
          metaMap,
          defaultValues,
          resolveValue,
        }),
      );

      mappedElem = mappedElem.set(layout, effectsData);
    });

    const customizedEffectsArray = computeCustomizedEffects(mappedElem, customizableEffects);
    mappedElem = mappedElem
      .set("customizedEffects", customizedEffectsArray)
      .set("removeCustomizations", onCustomizationRemoval);

    return mappedElem;
  };
};
